import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Loader2, CheckCircle2, AlertCircle, RefreshCw, Clock } from "lucide-react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { SupportContactStrip } from "./SupportContactStrip";

function stripePlatformProfileUrl(status) {
    return status?.platform?.profile_url
        || (status?.test_mode
            ? "https://dashboard.stripe.com/test/settings/connect/platform-profile"
            : "https://dashboard.stripe.com/settings/connect/platform-profile");
}

function stripeConnectHomeUrl(status) {
    return status?.platform?.connect_home_url
        || (status?.test_mode
            ? "https://dashboard.stripe.com/test/connect"
            : "https://dashboard.stripe.com/connect");
}

function StatusBadge({ phase }) {
    if (phase === "ready") {
        return (
            <span className="fs-pill gap-1 rounded-full border border-[#00BF0D]/25 bg-[#00BF0D]/10 px-2.5 py-0.5 text-[10px] font-semibold text-[#00BF0D]">
                <CheckCircle2 className="h-3 w-3" /> Connected
            </span>
        );
    }
    if (phase === "pending" || phase === "onboarding") {
        return (
            <span className="fs-pill gap-1 rounded-full border border-sky-500/25 bg-sky-500/10 px-2.5 py-0.5 text-[10px] font-semibold text-sky-300">
                <Clock className="h-3 w-3" /> In progress
            </span>
        );
    }
    return (
        <span className="fs-pill gap-1 rounded-full border border-amber-500/25 bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-semibold text-amber-300">
            <AlertCircle className="h-3 w-3" /> Not connected
        </span>
    );
}

export function StripeConnectSettings({ orgId, onStatusChange, syncToken, canOnboard = true }) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [busy, setBusy] = useState(false);
    const [disconnecting, setDisconnecting] = useState(false);

    const load = useCallback(async (forceSync = false) => {
        if (!orgId) return;
        if (forceSync) setRefreshing(true);
        else setLoading(true);
        try {
            if (forceSync) {
                const { data } = await api.post(`/orgs/${orgId}/connect/sync`);
                setStatus(data);
                onStatusChange?.(data);
                toast.success("Stripe status refreshed", {
                    description: data.ready
                        ? "Connect is active — you can bill clients."
                        : (data.message || "Check the status below for next steps."),
                    duration: 5000,
                });
                return data;
            }
            const { data } = await api.get(`/orgs/${orgId}/connect/status`);
            setStatus(data);
            onStatusChange?.(data);
            return data;
        } catch (err) {
            const msg = err.response?.data?.detail?.message || err.response?.data?.detail || err.message;
            toast.error(msg, { duration: 10000 });
            return null;
        } finally {
            if (forceSync) setRefreshing(false);
            else setLoading(false);
        }
    }, [orgId, onStatusChange]);

    useEffect(() => { load(false); }, [load]);

    useEffect(() => {
        if (!syncToken || !orgId) return;
        (async () => {
            const data = await load(true);
            if (!data) return;
            if (data.ready) {
                toast.success("Stripe Connect is active", { description: "You can bill clients from your pools." });
            } else if (data.phase === "pending" || data.phase === "onboarding") {
                toast.info("Almost there", {
                    description: data.message || "Stripe still needs a few details before payments are enabled.",
                    duration: 12000,
                });
            }
        })();
    }, [syncToken, orgId, load]);

    const startOnboarding = async () => {
        try {
            setBusy(true);
            const { data } = await api.post(`/orgs/${orgId}/connect/onboard`, {
                origin_url: window.location.origin,
            });
            toast.success("Opening Stripe…", { description: "Complete any remaining steps in Stripe." });
            window.location.href = data.url;
        } catch (err) {
            const code = err.response?.data?.detail?.code;
            if (code === "connect_ready") {
                await load(true);
                toast.success("Already connected");
                return;
            }
            const msg = err.response?.data?.detail?.message || err.response?.data?.detail || err.message;
            toast.error(msg, { duration: 10000 });
        } finally {
            setBusy(false);
        }
    };

    const disconnectStripe = async () => {
        const confirmed = window.confirm(
            "Disconnect Stripe from this workspace? You won't be able to bill clients or send payouts through Stripe until you connect again.",
        );
        if (!confirmed) return;
        try {
            setDisconnecting(true);
            const { data } = await api.post(`/orgs/${orgId}/connect/disconnect`);
            setStatus(data);
            onStatusChange?.(data);
            toast.success("Stripe disconnected", {
                description: "Connect again anytime to bill clients and run Stripe payouts.",
            });
        } catch (err) {
            const msg = err.response?.data?.detail?.message || err.response?.data?.detail || err.message;
            toast.error(msg, { duration: 10000 });
        } finally {
            setDisconnecting(false);
        }
    };

    if (loading && !status) {
        return (
            <div className="flex items-center gap-2 text-sm text-white/45 py-2">
                <Loader2 className="h-4 w-4 animate-spin" /> Checking Stripe Connect…
            </div>
        );
    }

    const phase = status?.phase || (status?.ready ? "ready" : "not_started");
    const platformReady = status?.platform?.ready !== false;
    const platformBlocked = status?.configured && !platformReady;
    const platformProfileUrl = stripePlatformProfileUrl(status);
    const stripeConnectHome = stripeConnectHomeUrl(status);
    const platformKeyMode = status?.platform?.key_mode;
    const staleLink = !!status?.sync_error && platformReady && !status?.account_id;
    const hasAccount = !!status?.account_id;
    const continueLabel = hasAccount ? "Continue Stripe setup" : "Connect Stripe account";

    const disconnectButton = (className, testId) => (
        canOnboard && (hasAccount || phase === "ready") ? (
            <button
                type="button"
                onClick={disconnectStripe}
                disabled={disconnecting || busy || refreshing}
                className={className}
                data-testid={testId}
            >
                {disconnecting ? "Disconnecting…" : "Disconnect"}
            </button>
        ) : null
    );

    const refreshButton = (className, testId, size = "sm") => (
        <button
            type="button"
            onClick={() => load(true)}
            disabled={refreshing || loading}
            className={className}
            data-testid={testId}
            aria-busy={refreshing}
        >
            {refreshing ? (
                <Loader2 className={size === "xs" ? "h-3 w-3 animate-spin" : "h-4 w-4 animate-spin"} />
            ) : (
                <RefreshCw className={size === "xs" ? "h-3 w-3" : "h-4 w-4"} />
            )}
            {refreshing ? "Refreshing…" : "Refresh status"}
        </button>
    );

    return (
        <div className="space-y-4" data-testid="stripe-connect-settings">
            <div className="flex flex-wrap items-center gap-2">
                <StatusBadge phase={phase} />
                {status?.test_mode && (
                    <span className="text-[10px] text-white/35 font-mono">Test mode</span>
                )}
                {status?.platform?.platform_country && (
                    <span className="text-[10px] text-white/35 font-mono">Platform: {status.platform.platform_country}</span>
                )}
            </div>

            {platformBlocked && (
                <div className="rounded-lg border border-amber-500/30 bg-amber-500/[0.08] p-3.5 space-y-3" data-testid="platform-connect-blocked">
                    <p className="text-sm font-medium text-amber-200">Stripe Connect platform setup isn&apos;t finished yet</p>
                    <p className="text-xs text-white/60">{status.platform?.reason}</p>
                    {(status?.test_mode || platformKeyMode === "test" || platformKeyMode === "live") && (
                        <p className="text-xs text-white/55">
                            FairShare&apos;s server is using Stripe{" "}
                            <strong className="font-normal text-white/75">
                                {platformKeyMode === "live" || (!status?.test_mode && platformKeyMode !== "test") ? "Live" : "Test"}
                            </strong>{" "}
                            mode keys. Complete platform profile in the same mode (toggle &ldquo;Test mode&rdquo; in the Stripe dashboard).
                        </p>
                    )}
                    <p className="text-xs text-white/50">
                        This is a one-time setup on <strong className="font-normal text-white/70">your</strong> Stripe account (FairShare&apos;s platform key), not the workspace Connect button below.
                        Complete the platform profile and loss responsibilities, then click Refresh.
                    </p>
                    <div className="flex flex-wrap gap-2">
                        <a
                            href={platformProfileUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2"
                            data-testid="connect-platform-profile-link"
                        >
                            <ExternalLink className="h-4 w-4" />
                            Complete platform profile in Stripe
                        </a>
                        <a
                            href={stripeConnectHome}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-ghost rounded-md px-3 py-2 text-sm inline-flex items-center gap-2"
                        >
                            Open Connect dashboard
                        </a>
                        {refreshButton(
                            "btn-ghost rounded-md px-3 py-2 text-sm inline-flex items-center gap-2",
                            "connect-platform-refresh",
                        )}
                    </div>
                </div>
            )}

            {staleLink && (
                <div className="rounded-lg border border-sky-500/30 bg-sky-500/[0.08] p-3.5 space-y-2" data-testid="connect-stale-link">
                    <p className="text-sm font-medium text-sky-200">Previous Stripe link was cleared</p>
                    <p className="text-xs text-white/60">{status.sync_error}</p>
                </div>
            )}

            {phase === "ready" ? (
                <div className="rounded-lg border border-[#00BF0D]/15 bg-[#00BF0D]/[0.04] p-3.5 space-y-2">
                    <p className="text-sm text-white/75">
                        Client card payments land in this workspace&apos;s Stripe account. FairShare tracks pool splits; approved payouts can be sent through Stripe to members who have completed payout setup.
                    </p>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-white/45 font-mono">
                        <span>Charges: on</span>
                        <span>Payouts: {status.payouts_enabled ? "on" : "off"}</span>
                        {status.account_id && <span className="truncate max-w-full">{status.account_id}</span>}
                    </div>
                    <div className="flex flex-wrap items-center gap-2 pt-0.5">
                        {refreshButton(
                            "btn-ghost rounded-md px-2.5 py-1 text-xs inline-flex items-center gap-1.5 disabled:opacity-50",
                            "connect-refresh-status",
                            "xs",
                        )}
                        {disconnectButton(
                            "text-xs text-white/40 hover:text-rose-300/90 disabled:opacity-50 px-1",
                            "connect-disconnect-btn",
                        )}
                    </div>
                </div>
            ) : (
                <div className="rounded-lg border border-white/[0.08] bg-white/[0.02] p-3.5 space-y-3">
                    {status?.message && (
                        <p className="text-sm text-white/70">{status.message}</p>
                    )}

                    {hasAccount && (
                        <div className="rounded-lg border border-sky-500/20 bg-sky-500/[0.06] p-3 space-y-2" data-testid="connect-in-progress">
                            <p className="text-xs text-sky-200/90 font-medium">
                                Stripe account linked — payments aren&apos;t enabled yet.
                            </p>
                            {status.requirements_due_labels?.length > 0 && (
                                <ul className="text-xs text-white/55 list-disc pl-4 space-y-0.5">
                                    {status.requirements_due_labels.map((label) => (
                                        <li key={label}>{label}</li>
                                    ))}
                                </ul>
                            )}
                            <p className="text-[11px] text-white/45">
                                Stripe&apos;s &ldquo;success&rdquo; screen can appear before everything is done. Click Continue below to fill in what&apos;s missing.
                            </p>
                        </div>
                    )}

                    {!hasAccount && (
                        <p className="text-sm text-white/65">
                            Required to bill clients by card from a pool and to send approved payouts through Stripe.
                        </p>
                    )}

                    <div className="flex flex-wrap items-center gap-2">
                        {canOnboard && !platformBlocked ? (
                        <button
                            type="button"
                            onClick={startOnboarding}
                            disabled={busy || !status?.configured}
                            className="btn-neon rounded-md px-4 py-2 text-sm inline-flex items-center gap-2"
                            data-testid="connect-stripe-btn"
                        >
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <ExternalLink className="h-4 w-4" />}
                            {busy ? "Opening Stripe…" : continueLabel}
                        </button>
                        ) : !canOnboard ? (
                            <p className="text-xs text-white/50">Only the workspace owner can connect Stripe for this organisation.</p>
                        ) : null}
                        {hasAccount && refreshButton(
                            "btn-ghost rounded-md px-3 py-2 text-sm inline-flex items-center gap-2 disabled:opacity-50",
                            "connect-refresh-status",
                        )}
                        {disconnectButton(
                            "text-xs text-white/40 hover:text-rose-300/90 disabled:opacity-50 px-1 py-2",
                            "connect-disconnect-btn",
                        )}
                    </div>

                    {!status?.configured && (
                        <p className="text-[11px] text-amber-300/80">Stripe is not configured on this server yet.</p>
                    )}
                    <SupportContactStrip variant="billing" className="pt-1" />
                </div>
            )}
        </div>
    );
}

export function useConnectStatus(orgId) {
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(!!orgId);

    const refresh = useCallback(async () => {
        if (!orgId) return null;
        setLoading(true);
        try {
            const { data } = await api.post(`/orgs/${orgId}/connect/sync`);
            setStatus(data);
            return data;
        } catch (_e) {
            setStatus(null);
            return null;
        } finally {
            setLoading(false);
        }
    }, [orgId]);

    useEffect(() => {
        if (!orgId) {
            setStatus(null);
            setLoading(false);
            return;
        }
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const { data } = await api.get(`/orgs/${orgId}/connect/status`);
                if (!cancelled) setStatus(data);
            } catch (_e) {
                if (!cancelled) setStatus(null);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
    }, [orgId]);

    return { status, loading, ready: !!status?.ready, refresh };
}
